import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  getThread,
  createReply,
  deleteReply,
  deleteThread,
} from '../services/api';
import './Forum.css';

interface Reply {
  id: number;
  thread_id: number;
  user_id: number;
  username: string;
  role: string;
  body: string;
  created_at: string;
}

interface Thread {
  id: number;
  user_id: number;
  username: string;
  title: string;
  body: string;
  created_at: string;
  reply_count: number;
  replies: Reply[];
}

const ForumThread: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [thread, setThread] = useState<Thread | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getThread(Number(id));
      setThread(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!replyBody.trim() || !id) return;
    setSubmitting(true);
    try {
      await createReply(Number(id), { body: replyBody.trim() });
      setReplyBody('');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not send reply.');
    } finally {
      setSubmitting(false);
    }
  };

  const removeReply = async (rid: number) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await deleteReply(rid);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const removeThread = async () => {
    if (!thread) return;
    if (!window.confirm('Delete this entire thread?')) return;
    try {
      await deleteThread(thread.id);
      navigate('/forum');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="forum-page">
        <div className="loading-container" style={{ paddingTop: 120 }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="forum-page">
        <div className="container" style={{ paddingTop: 140, textAlign: 'center' }}>
          <p>Thread not found.</p>
          <Link to="/forum" className="btn btn-outline" style={{ marginTop: 16 }}>
            Back to Forum
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="forum-page">
      <section className="forum-thread-section section">
        <div className="container" style={{ maxWidth: 820 }}>
          <Link to="/forum" className="forum-back">&larr; Back to Forum</Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="thread-detail"
          >
            <div className="thread-detail-head">
              <h1>{thread.title}</h1>
              {(isAdmin || user?.id === thread.user_id) && (
                <button className="btn-sm btn-danger" onClick={removeThread}>
                  Delete thread
                </button>
              )}
            </div>
            <div className="thread-detail-meta">
              <span className="thread-author">{thread.username}</span>
              <span className="thread-dot">&bull;</span>
              <span>{new Date(thread.created_at).toLocaleString()}</span>
            </div>
            <div className="thread-detail-body">
              {thread.body.split('\n').map((line, i) => (
                <p key={i}>{line || <br />}</p>
              ))}
            </div>
          </motion.div>

          <div className="replies-section">
            <h3 className="replies-heading">
              {thread.replies.length} {thread.replies.length === 1 ? 'reply' : 'replies'}
            </h3>

            {thread.replies.length === 0 ? (
              <p className="replies-empty">No replies yet. Be the first to respond.</p>
            ) : (
              <div className="replies-list">
                {thread.replies.map((r, i) => (
                  <motion.div
                    key={r.id}
                    className={`reply-card ${r.role === 'admin' ? 'reply-from-admin' : ''}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                  >
                    <div className="reply-head">
                      <span className="reply-author">
                        {r.username}
                        {r.role === 'admin' && <span className="author-badge">Author</span>}
                      </span>
                      <span className="reply-date">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <div className="reply-body">
                      {r.body.split('\n').map((line, j) => (
                        <p key={j}>{line || <br />}</p>
                      ))}
                    </div>
                    {(isAdmin || user?.id === r.user_id) && (
                      <button className="reply-delete" onClick={() => removeReply(r.id)}>
                        Delete
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="reply-form-wrap">
            {user ? (
              <form onSubmit={submit} className="reply-form">
                <label className="form-label">
                  {isAdmin ? 'Respond as Kutloano' : 'Your reply'}
                </label>
                <textarea
                  className="form-input form-textarea"
                  style={{ minHeight: 120 }}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Write a reply..."
                />
                {error && <div className="form-error">{error}</div>}
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send reply'}
                </button>
              </form>
            ) : (
              <div className="reply-signin">
                <p>Sign in to join the conversation.</p>
                <Link to="/login" className="btn btn-primary">Sign In</Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForumThread;
